import { Menu } from 'primereact/menu';
import { useRef } from 'react';

import { BaseButton, type BaseButtonVariant } from './BaseButton';

export interface MenuItem {
  label: string;
  icon?: string;
  onClick: () => void;
  disabled?: boolean;
  /** Destructive action (shown in red). */
  danger?: boolean;
}

export interface BaseMenuButtonProps {
  label: string;
  items: MenuItem[];
  icon?: string;
  variant?: BaseButtonVariant;
  size?: 'sm' | 'md';
  loading?: boolean;
  /** Icon-only button; `label` becomes its accessible name and tooltip. */
  hideLabel?: boolean;
  /** Smaller, tighter menu, for row actions in dense lists. */
  compact?: boolean;
}

/** A button that opens a small menu (e.g. Export → CSV / Excel). */
export function BaseMenuButton({
  label,
  items,
  icon,
  variant = 'secondary',
  size = 'md',
  loading,
  hideLabel,
  compact,
}: BaseMenuButtonProps) {
  const menu = useRef<Menu>(null);
  return (
    <>
      <Menu
        ref={menu}
        popup
        className={compact ? 'cd-menu-compact' : undefined}
        model={items.map((i) => ({
          label: i.label,
          icon: i.icon,
          disabled: i.disabled,
          className: i.danger ? 'menu-danger' : undefined,
          command: i.onClick,
        }))}
      />
      <BaseButton
        variant={variant}
        size={size}
        icon={icon}
        loading={loading}
        onClick={(e) => {
          e.stopPropagation(); // menus sit inside clickable rows
          menu.current?.toggle(e);
        }}
        aria-haspopup
        aria-label={hideLabel ? label : undefined}
        title={hideLabel ? label : undefined}
      >
        {hideLabel ? null : label}
      </BaseButton>
    </>
  );
}
